import { readonly, ref, onBeforeUnmount } from 'vue';
import { ScanKeyEvent, Shopflow, ScanRfidEvent, PLUGIN_EVENTS, EVENTS, useDeviceInfo } from './index';

export type ScanMethod = 'single' | 'multiple' | 'singleShort';

const isScanning = ref(false);
const method = ref<ScanMethod>('singleShort');
const multipleTimeout = ref<number | null>(null);
const removeKeyEventListenerCallback = ref<() => void | null>(null);
const rfidEventListenerCallback = ref<() => Promise<void> | null>(null);

export const useReadRfid = () => {
  const { deviceInfo } = useDeviceInfo();

  const unsubscribeCallback = ref<() => void | null>(null);

  const removeKeyEventListener = async () => {
    if (!deviceInfo.value?.isRfidReader) return;

    if (removeKeyEventListenerCallback.value) {
      removeKeyEventListenerCallback.value();
      removeKeyEventListenerCallback.value = null;
    }
  }

  const removeRfidEventListener = async () => {
    if (!deviceInfo.value?.isRfidReader) return;

    isScanning.value = false;
    Shopflow.stopMultiReadRfid();

    if (rfidEventListenerCallback.value) {
      rfidEventListenerCallback.value();
      rfidEventListenerCallback.value = null;
    }
  }

  const sendEvent = (event: ScanRfidEvent) => {
    window.dispatchEvent(new CustomEvent(EVENTS.SCAN_RFID_EVENT, { detail: event }));
  }

  const addKeyEventListener = async () => {
    if (!deviceInfo.value?.isRfidReader) return;

    removeKeyEventListener();

    let waitUp = false;

    const listener = async (event: CustomEvent<ScanKeyEvent>) => {
      if (event.detail.code !== deviceInfo.value.rfidGunKeyCode) {
        return;
      }

      const isKeyDown = event.detail.action === 'down';

      if (method.value === 'singleShort') {
        if (!isKeyDown) {
          waitUp = false;
          return;
        }

        if (waitUp) {
          return;
        }

        const { value } = await Shopflow.onceReadBankEPC();

        sendEvent({ value });

        waitUp = true;
      } else if (method.value === 'single') {
        if (!isKeyDown) {
          waitUp = false;
          return;
        }

        if (waitUp) {
          return;
        }

        const { value, power } = await Shopflow.onceReadRfid();
        
        if (value) {
          sendEvent({ value, power });
        }
        
        waitUp = true;
      } else if (method.value === 'multiple') {
        if (isKeyDown) {
          if (waitUp) {
            return;
          }

          if (isScanning.value) {
            removeRfidEventListener();
            isScanning.value = false;

            return;
          }

          isScanning.value = true;

          const { remove } = await Shopflow.addListener(PLUGIN_EVENTS.SCAN_RFID_EVENT, (event: ScanRfidEvent) => {
            sendEvent(event);
          });

          rfidEventListenerCallback.value = remove;

          Shopflow.startMultiReadRfid();

          if (multipleTimeout.value) {
            setTimeout(() => {
              removeRfidEventListener();
            }, multipleTimeout.value);
          }

          waitUp = true;
        } else {
          waitUp = false;
        }
      }
    };

    window.addEventListener(EVENTS.SCAN_KEY_EVENT, listener);

    removeKeyEventListenerCallback.value = () => {
      window.removeEventListener(EVENTS.SCAN_KEY_EVENT, listener);
    }
  }

  const setMethod = (newMethod: ScanMethod, timeout?: number) => {
    if (!deviceInfo.value?.isRfidReader) return;

    multipleTimeout.value = newMethod === "multiple" ? timeout : null;
    method.value = newMethod;

    removeRfidEventListener();
  }

  const subscribe = (callback: (event: ScanRfidEvent) => void) => {
    if (!deviceInfo.value?.isRfidReader) return;

    unsubscribe();

    const listener = (event: CustomEvent<ScanRfidEvent>) => {
      callback(event.detail);
    }

    window.addEventListener(EVENTS.SCAN_RFID_EVENT, listener);

    unsubscribeCallback.value = () => {
      window.removeEventListener(EVENTS.SCAN_RFID_EVENT, listener);
    }
  }

  const unsubscribe = () => {
    if (!deviceInfo.value?.isRfidReader) return;
    
    if (unsubscribeCallback.value) {
      unsubscribeCallback.value();
      unsubscribeCallback.value = null;
    }
  }

  const onceReadBankEPC = async () => {
    if (!deviceInfo.value?.isRfidReader) return;

    const { value } = await Shopflow.onceReadBankEPC();

    return value;
  }

  onBeforeUnmount(()=> {
    unsubscribe();
  });
  
  return {
    method: readonly(method),
    isScanning: readonly(isScanning),
    addKeyEventListener,
    removeKeyEventListener,
    setMethod,
    subscribe,
    unsubscribe,
    onceReadBankEPC,
  }
}